import React, { useEffect, useState, useRef } from "react";
import API from "../services/api";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "../styles/billing.css";

export default function Dues() {
  const [dues, setDues] = useState([]);
  const [search, setSearch] = useState("");
  const popupCountRef = useRef(0);
  const stopAllPopupsRef = useRef(false);

  useEffect(() => {
    fetchDues();
  }, [search]);

  const fetchDues = async () => {
    const res = await API.get(`/billing/dues?search=${search}`);
    if (res.data.success) setDues(res.data.dues);
  };

  /* ---------------- DAY COLOR LOGIC ---------------- */
  const getDueColor = (date) => {
    const days = Math.floor(
      (new Date() - new Date(date)) / (1000 * 60 * 60 * 24)
    );

    if (days === 1) return "#fb923c";
    if (days === 2) return "#facc15";
    if (days >= 3) return "#ef4444";
    return "#38bdf8";
  };

  /* ---------------- PAY DUE ---------------- */
  // const payDue = async (inv) => {
  //   const { value } = await Swal.fire({
  //     title: "💰 Pay Due Amount",
  //     input: "number",
  //     inputLabel: `Pending Due: ₹${inv.dueAmount}`,
  //     inputAttributes: { min: 1 },
  //     showCancelButton: true,
  //     confirmButtonColor: "#22c55e",
  //   });

  //   if (!value) return;

  //   await API.post("/billing/pay-due", {
  //     invoiceId: inv._id,
  //     paidNow: Number(value),
  //   });

  //   Swal.fire("Success", "Payment Updated", "success");
  //   fetchDues();
  // };

  const payDue = async (inv) => {
    const { value } = await Swal.fire({
      title: "💰 Pay Due Amount",
      input: "number",
      inputLabel: `Pending Due: ₹${inv.dueAmount}`,
      inputAttributes: { min: 1 },
      showCancelButton: true,
      confirmButtonColor: "#22c55e",
    });

    if (!value) return;

    const payAmount = Number(value);

    // ❌ INVALID INPUT
    if (payAmount <= 0) {
      return Swal.fire("Error", "Enter valid amount", "error");
    }

    // 🔥 MAIN FIX (OVERPAY PROTECTION)
    if (payAmount > inv.dueAmount) {
      return Swal.fire({
        icon: "warning",
        title: "Amount Exceeded ⚠️",
        text: `You entered ₹${payAmount} but due is only ₹${inv.dueAmount}`,
        confirmButtonColor: "#f59e0b",
      });
    }

    // ✅ VALID PAYMENT
    await API.post("/billing/pay-due", {
      invoiceId: inv._id,
      paidNow: payAmount,
    });

    Swal.fire("Success", "Payment Updated", "success");
    fetchDues();
  };

  /* ---------------- PDF ---------------- */
  const downloadPDF = async (inv) => {
    if (!inv) return;

    const doc = new jsPDF("p", "mm", "a4");

    const money = (v) => `Rs. ${Number(v || 0).toFixed(2)}`;
    const billDate = new Date(inv.date).toLocaleDateString("en-IN");

    // ===== HEADER =====
    doc.setFillColor(12, 18, 36);
    doc.rect(0, 0, 210, 38, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("BHOPAL SEAT COVER", 105, 16, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Premium Car Seat Covers & Accessories", 105, 24, { align: "center" });

    doc.setFillColor(220, 38, 38);
    doc.roundedRect(72, 29, 66, 7, 2, 2, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("PAYMENT DUE REMINDER", 105, 34, { align: "center" });

    // ===== CUSTOMER BOX =====
    autoTable(doc, {
      startY: 48,
      theme: "grid",
      head: [["Customer Details", "Invoice Details"]],
      body: [
        [`Name: ${inv.customerName || "-"}`, `Bill Date: ${billDate}`],
        [`Mobile: ${inv.customerMobile || "N/A"}`, `Invoice ID: ${inv._id?.slice(-6) || "-"}`],
        [`Status: Pending Due`, `Due Amount: ${money(inv.dueAmount)}`],
      ],
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
    });

    // ===== ITEMS TABLE =====
    const itemRows = (inv.items || []).map((item, index) => {
      const qty = Number(item.qty || 0);
      const price = Number(item.price || 0);

      return [
        index + 1,
        item.name || "-",
        `${qty} ${item.unit || ""}`,
        money(price),
        money(qty * price),
      ];
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      theme: "striped",
      head: [["#", "Item Name", "Qty / Unit", "Rate", "Amount"]],
      body: itemRows.length ? itemRows : [["-", "No items found", "-", "-", "-"]],
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 12, halign: "center" },
        1: { cellWidth: 78 },
        2: { cellWidth: 28, halign: "center" },
        3: { cellWidth: 28, halign: "right" },
        4: { cellWidth: 34, halign: "right" },
      },
    });

    // ===== SUMMARY =====
    const summaryY = doc.lastAutoTable.finalY + 10;

    autoTable(doc, {
      startY: summaryY,
      theme: "grid",
      body: [
        ["Subtotal", money(inv.subTotal)],
        ["Discount", money(inv.discount)],
        ["Total Amount", money(inv.totalAmount)],
        ["Paid Amount", money(inv.paidAmount)],
        ["Balance Due", money(inv.dueAmount)],
      ],
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 130 },
        1: { halign: "right", cellWidth: 50 },
      },
      didParseCell: function (data) {
        if (data.row.index === 4) {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = "bold";
        }
      },
      margin: { left: 14 },
    });

    // ===== NOTE BOX =====
    const noteY = doc.lastAutoTable.finalY + 12;

    doc.setFillColor(254, 242, 242);
    doc.setDrawColor(220, 38, 38);
    doc.roundedRect(14, noteY, 182, 22, 3, 3, "FD");

    doc.setTextColor(220, 38, 38);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Payment Reminder", 18, noteY + 8);

    doc.setTextColor(40, 40, 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Please clear your pending due amount as soon as possible.", 18, noteY + 15);

    // ===== FOOTER =====
    doc.setTextColor(120);
    doc.setFontSize(8);
    doc.text("Thank you for choosing Bhopal Seat Cover.", 105, 282, { align: "center" });
    doc.text("This is a computer generated invoice.", 105, 287, { align: "center" });

    doc.save(`${inv.customerName || "Customer"}_Due_Invoice.pdf`);

    if (!inv.customerMobile || inv.customerMobile === "N/A") return;

    const wa = await Swal.fire({
      title: "Send WhatsApp Reminder?",
      text: "PDF download ho chuki hai. WhatsApp message bhejna hai?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Send WhatsApp",
      cancelButtonText: "No",
      confirmButtonColor: "#22c55e",
    });

    if (!wa.isConfirmed) return;

    const itemText = (inv.items || [])
      .map((item, i) => {
        const qty = `${item.qty || 0} ${item.unit || ""}`;
        const amount = Number(item.qty || 0) * Number(item.price || 0);
        return `${i + 1}. ${item.name} - ${qty} x Rs. ${item.price} = Rs. ${amount}`;
      })
      .join("\n");

    const msg =
      `*BHOPAL SEAT COVER*
Payment Reminder

Hello *${inv.customerName}*,

Your pending bill details:

*Bill Date:* ${billDate}

*Items:*
${itemText}

*Total Amount:* Rs. ${inv.totalAmount}
*Paid Amount:* Rs. ${inv.paidAmount}
*Pending Due:* Rs. ${inv.dueAmount}

Please jaldi due ko paid kro.

Thank you.`;

    window.open(
      `https://wa.me/91${inv.customerMobile}?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };


  /* ---------------- ADMIN POPUP ---------------- */
  const ADMIN = {
    name: "Mohammad Rashid Khan",
    mobile: "+91 98765 43210",
  };

  const totalDueAmount = dues.reduce((sum, d) => sum + d.dueAmount, 0);
  const totalDueCount = dues.length;

  useEffect(() => {
    const lateDues = dues.filter((d) => {
      const days =
        Math.floor((new Date() - new Date(d.date)) / (1000 * 60 * 60 * 24));
      return days >= 3;
    });

    if (lateDues.length === 0) return;

    // ---- ADMIN SUMMARY (SAME AS BEFORE) ----
    Swal.fire({
      title: "🔴 3+ Days Pending Dues",
      html: `
      <b>Admin:</b> ${ADMIN.name}<br/>
      <b>Mobile:</b> ${ADMIN.mobile}<br/><br/>
      <b>Total Dues:</b> ${lateDues.length}<br/>
      <b>Total Amount:</b> ₹${lateDues.reduce(
        (s, d) => s + d.dueAmount,
        0
      )}
    `,
      icon: "warning",
      confirmButtonText: "OK",
      confirmButtonColor: "#ef4444",
    }).then(async () => {

      // ---- CUSTOMER WISE POPUPS ----
      for (const d of lateDues) {

        if (stopAllPopupsRef.current) break;   // ✅ ADD THIS LINE

        const days =
          Math.floor((new Date() - new Date(d.date)) / (1000 * 60 * 60 * 24));

        const res = await Swal.fire({
          title: "⏰ Payment Late",
          html: `
          <b>Customer:</b> ${d.customerName}<br/>
          <b>Mobile:</b> ${d.customerMobile}<br/>
          <b>Due Amount:</b> ₹${d.dueAmount}<br/>
          <b>Late By:</b> ${days} days
        `,
          icon: "info",

          showConfirmButton: true,
          showDenyButton: true,
          showCancelButton: true,

          confirmButtonText: "📄 WhatsApp PDF",   // 1️⃣
          denyButtonText: "⏭ Skip",              // 2️⃣
          cancelButtonText: "❌ Close All",       // 3️⃣

          confirmButtonColor: "#22c55e",
          denyButtonColor: "#facc15",
          cancelButtonColor: "#ef4444",
        });

        /* ---------- BUTTON ACTIONS ---------- */
        if (res.isConfirmed) {
          // WhatsApp + PDF
          await sendCustomerPDFOnWhatsApp(d);
        }

        if (res.isDenied) {
          // Skip → sirf current popup close
          // kuch mat karo, next customer pe chala jaayega
        }

        if (res.dismiss === Swal.DismissReason.cancel) {
          // Close All → saare popup band
          stopAllPopupsRef.current = true;
        }
      }
    });
  }, [dues]);


  const sendCustomerPDFOnWhatsApp = async (inv) => {
    if (!inv.customerMobile || inv.customerMobile === "N/A") {
      return Swal.fire("Error", "Customer ka mobile number nahi hai", "error");
    }

    const doc = new jsPDF("p", "mm", "a4");

    const money = (v) => `Rs. ${Number(v || 0).toFixed(2)}`;
    const billDate = new Date(inv.date).toLocaleDateString("en-IN");

    // ===== HEADER =====
    doc.setFillColor(12, 18, 36);
    doc.rect(0, 0, 210, 38, "F");

    doc.setTextColor(255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("BHOPAL SEAT COVER", 105, 16, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Premium Car Seat Covers & Accessories", 105, 24, { align: "center" });

    doc.setFillColor(220, 38, 38);
    doc.roundedRect(68, 29, 74, 7, 2, 2, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("PAYMENT DUE REMINDER", 105, 34, { align: "center" });

    // ===== CUSTOMER DETAILS =====
    autoTable(doc, {
      startY: 48,
      theme: "grid",
      head: [["Customer Details", "Invoice Details"]],
      body: [
        [`Name: ${inv.customerName || "-"}`, `Bill Date: ${billDate}`],
        [`Mobile: +91 ${inv.customerMobile || "N/A"}`, `Invoice ID: ${inv._id?.slice(-6) || "-"}`],
        [`Status: Pending Due`, `Due Amount: ${money(inv.dueAmount)}`],
      ],
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
    });

    // ===== ITEMS =====
    const itemRows = (inv.items || []).map((item, index) => {
      const qty = Number(item.qty || 0);
      const price = Number(item.price || 0);

      return [
        index + 1,
        item.name || "-",
        `${qty} ${item.unit || ""}`,
        money(price),
        money(qty * price),
      ];
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      theme: "striped",
      head: [["#", "Item Name", "Qty / Unit", "Rate", "Amount"]],
      body: itemRows.length ? itemRows : [["-", "No items found", "-", "-", "-"]],
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 12, halign: "center" },
        1: { cellWidth: 78 },
        2: { cellWidth: 28, halign: "center" },
        3: { cellWidth: 28, halign: "right" },
        4: { cellWidth: 34, halign: "right" },
      },
    });

    // ===== SUMMARY =====
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      theme: "grid",
      body: [
        ["Subtotal", money(inv.subTotal)],
        ["Discount", money(inv.discount)],
        ["Total Amount", money(inv.totalAmount)],
        ["Paid Amount", money(inv.paidAmount)],
        ["Pending Due", money(inv.dueAmount)],
      ],
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 130 },
        1: { halign: "right", cellWidth: 50 },
      },
      didParseCell: function (data) {
        if (data.row.index === 4) {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = "bold";
        }
      },
      margin: { left: 14 },
    });

    // ===== REMINDER BOX =====
    const noteY = doc.lastAutoTable.finalY + 12;

    doc.setFillColor(254, 242, 242);
    doc.setDrawColor(220, 38, 38);
    doc.roundedRect(14, noteY, 182, 24, 3, 3, "FD");

    doc.setTextColor(220, 38, 38);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Payment Reminder", 18, noteY + 8);

    doc.setTextColor(40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      "Kindly clear your pending due amount as soon as possible.",
      18,
      noteY + 15
    );

    doc.text(
      "Your quick payment will help us keep your account updated.",
      18,
      noteY + 20
    );

    // ===== FOOTER =====
    doc.setTextColor(120);
    doc.setFontSize(8);
    doc.text("Thank you for choosing Bhopal Seat Cover.", 105, 282, {
      align: "center",
    });
    doc.text("This is a computer generated payment reminder.", 105, 287, {
      align: "center",
    });

    doc.save(`${inv.customerName || "Customer"}_Payment_Reminder.pdf`);

    // ===== WHATSAPP MESSAGE =====
    const itemText = (inv.items || [])
      .map((item, index) => {
        const qty = `${item.qty || 0} ${item.unit || ""}`;
        const amount = Number(item.qty || 0) * Number(item.price || 0);

        return `${index + 1}. ${item.name} - ${qty} x Rs. ${item.price} = Rs. ${amount}`;
      })
      .join("\n");

    const msg =
      `*BHOPAL SEAT COVER*
*Payment Reminder*

Hello *${inv.customerName}*,

Your payment is still pending for the bill below:

*Bill Date:* ${billDate}

*Items:*
${itemText}

*Total Amount:* Rs. ${inv.totalAmount}
*Paid Amount:* Rs. ${inv.paidAmount}
*Pending Due:* Rs. ${inv.dueAmount}

Kindly clear your pending due amount as soon as possible.
Your quick payment will help us keep your account updated.

Thank you.
Bhopal Seat Cover`;

    window.open(
      `https://wa.me/91${inv.customerMobile}?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };


  return (
    <div className="dashboard-layout">
      <Sidebar />

      <div className="main-content">
        <div className="portal-header-action">
          <h2 className="title-glow">📒 Pending Dues</h2>
          <button className="bsc-toggle-btn" onClick={() => window.history.back()}>
            ⬅ Back
          </button>
        </div>

        <input
          className="bsc-input-search"
          placeholder="🔍 Search by name or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="table-responsive">
          <table className="bsc-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Total</th>
                <th>Discount</th>
                <th>Paid</th>
                <th>Due</th>
                <th>Date</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {dues.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: 30 }}>
                    No pending dues found 🙂
                  </td>
                </tr>
              )}

              {[...dues]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((d) => (
                  <tr
                    key={d._id}
                    style={{ borderLeft: `6px solid ${getDueColor(d.date)}` }}
                  >
                    <td>
                      <b>{d.customerName}</b>
                      <br />
                      <small style={{ color: "#64748b" }}>
                        {d.customerMobile}
                      </small>
                    </td>

                    <td>₹{d.totalAmount}</td>

                    <td style={{ fontWeight: 600, color: "#facc15" }}>
                      ₹{d.discount || 0}
                    </td>

                    <td style={{ color: "#22c55e", fontWeight: 600 }}>
                      ₹{d.paidAmount}
                    </td>

                    <td style={{ color: "#ef4444", fontWeight: 800 }}>
                      ₹{d.dueAmount}
                    </td>

                    <td>{new Date(d.date).toLocaleDateString()}</td>

                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        <button className="btn-view-items" onClick={() => payDue(d)}>
                          💰 Pay
                        </button>

                        <button
                          className="btn-pdf-report"
                          onClick={() => downloadPDF(d)}
                        // onClick={async () => {
                        //   const confirm = await Swal.fire({
                        //     title: "Download PDF?",
                        //     text: "Due bill download karna hai?",
                        //     icon: "question",
                        //     showCancelButton: true,
                        //   });

                        //   if (confirm.isConfirmed) {
                        //     downloadPDF(d);
                        //   }
                        // }}
                        >
                          📄 PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}
